import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft,
    MapPin,
    Phone,
    Mail,
    Clock,
    ArrowUpRight
} from 'lucide-react';

const ContactUs = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFDFD] text-slate-800 font-sans selection:bg-slate-200 flex flex-col">

            {/* --- Header / Navigation --- */}
            <div className="pt-12 px-6">
                <div className="container mx-auto max-w-5xl">
                    <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-8 group">
                        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium">返回首页</span>
                    </Link>
                </div>
            </div>

            {/* --- Main Content --- */}
            <div className="flex-1 px-6 pb-20 flex items-center">
                <div className="container mx-auto max-w-5xl">
                    <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">

                        {/* Left Column: Text & Info */}
                        <div className="animate-fade-in-up">
                            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                                联系我们
                            </h1>
                            <p className="text-xl text-slate-500 mb-12 max-w-md leading-relaxed">
                                无论是业务咨询还是合作洽谈，我们都期待与您通过以下方式建立连接。
                            </p>

                            <div className="space-y-8">
                                {/* Contact Item: Address */}
                                <ContactItem
                                    icon={<MapPin size={24} />}
                                    label="公司地址"
                                    value="上海市 XX区 XX路 123号 XX大厦 A座 808室"
                                    action="查看地图"
                                />

                                {/* Contact Item: Phone */}
                                <ContactItem
                                    icon={<Phone size={24} />}
                                    label="咨询热线"
                                    value="+86 21 1234 5678"
                                    subValue="工作日 09:00 - 18:00"
                                />

                                {/* Contact Item: Email */}
                                <ContactItem
                                    icon={<Mail size={24} />}
                                    label="电子邮箱"
                                    value="contact@fanxin.com"
                                    action="发送邮件"
                                />
                            </div>
                        </div>

                        {/* Right Column: Visual Card (Abstract Logo) */}
                        <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                            {/* 背景装饰块 */}
                            <div className="absolute top-0 right-0 w-full h-full bg-[#F3F6F8] rounded-[3rem] -rotate-3 scale-95 z-0"></div>

                            {/* 主卡片 */}
                            <div className="relative z-10 bg-slate-900 text-white rounded-[2.5rem] p-12 shadow-2xl overflow-hidden min-h-[500px] flex flex-col justify-between">

                                {/* 顶部：公司名 */}
                                <div>
                                    <p className="text-slate-400 text-sm tracking-widest uppercase font-bold mb-2">Shanghai Fanxin</p>
                                    <h2 className="text-2xl font-bold">繁昕财务咨询</h2>
                                </div>

                                {/* 中间：极简抽象人像 Logo (CSS绘制) */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center opacity-90">
                                    {/* 头部 (圆形) */}
                                    <div className="w-24 h-24 bg-slate-200 rounded-full mb-4 shadow-lg animate-float-slow"></div>
                                    {/* 身体 (半圆矩形) */}
                                    <div className="w-40 h-20 bg-slate-600 rounded-t-[5rem] shadow-lg animate-float-delayed"></div>
                                    {/* 装饰光环 */}
                                    <div className="absolute w-64 h-64 border border-slate-700 rounded-full animate-spin-slow opacity-30 -z-10"></div>
                                </div>

                                {/* 底部：Slogan */}
                                <div className="flex justify-between items-end">
                                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                                        <Clock size={16} />
                                        <span>Response within 24h</span>
                                    </div>
                                    <div className="w-12 h-12 bg-white text-slate-900 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                                        <ArrowUpRight size={24} />
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
        @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
            animation: spin-slow 20s linear infinite;
        }
      `}</style>
        </div>
    );
};

// 简单的联系方式组件
const ContactItem = ({ icon, label, value, subValue, action }) => (
    <div className="flex gap-6 group cursor-default">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-900 shrink-0 group-hover:bg-slate-200 transition-colors">
            {icon}
        </div>
        <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-lg font-medium text-slate-900 leading-tight">{value}</p>
            {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
            {action && (
                <button className="text-sm text-slate-500 font-medium mt-1 border-b border-transparent hover:border-slate-500 hover:text-slate-800 transition-all">
                    {action}
                </button>
            )}
        </div>
    </div>
);

export default ContactUs;